import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, TrendingUp, Calendar, CreditCard, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PERIOD_LABELS: Record<string, string> = {
  daily: '按日',
  weekly: '按週',
  monthly: '按月',
};

export function RevenuePage() {
  const [period, setPeriod] = useState('daily');
  const [txPage, setTxPage] = useState(1);
  const [txStatusFilter, setTxStatusFilter] = useState('all');
  const [txProviderFilter, setTxProviderFilter] = useState('all');

  const { data: revenue } = useQuery({
    queryKey: ['admin', 'revenue'],
    queryFn: () => adminClient.get('/admin/analytics/revenue').then((r) => r.data),
  });

  const { data: trend } = useQuery({
    queryKey: ['admin', 'revenue-trend', period],
    queryFn: () => adminClient.get(`/admin/analytics/revenue-trend?period=${period}`).then((r) => r.data),
  });

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['admin', 'payments', txPage, txStatusFilter, txProviderFilter],
    queryFn: () =>
      adminClient.get('/admin/payments', {
        params: {
          page: txPage,
          per_page: 20,
          status: txStatusFilter !== 'all' ? txStatusFilter : undefined,
          provider: txProviderFilter !== 'all' ? txProviderFilter : undefined,
        },
      }).then((r) => r.data),
  });

  const statCards = [
    { label: '總收入', value: formatCurrency(revenue?.total_revenue ?? 0), icon: DollarSign, color: 'text-green-500' },
    { label: '今日', value: formatCurrency(revenue?.revenue_today ?? 0), icon: Calendar, color: 'text-blue-500' },
    { label: '本週', value: formatCurrency(revenue?.revenue_this_week ?? 0), icon: TrendingUp, color: 'text-amber-500' },
    { label: '每用戶平均收入', value: formatCurrency(revenue?.arpu ?? 0), icon: CreditCard, color: 'text-purple-500' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">收入統計</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold mt-1">{value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue Trend */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">收入趨勢</CardTitle>
          <div className="flex gap-1">
            {['daily', 'weekly', 'monthly'].map((p) => (
              <Button key={p} variant={period === p ? 'default' : 'ghost'} size="sm" onClick={() => setPeriod(p)}>
                {PERIOD_LABELS[p]}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
              <Bar dataKey="amount" fill="#00d084" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Payment Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">付款紀錄</CardTitle>
          <div className="flex gap-2">
            <Select value={txStatusFilter} onValueChange={(v) => { setTxStatusFilter(v); setTxPage(1); }}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">所有狀態</SelectItem>
                <SelectItem value="success">成功</SelectItem>
                <SelectItem value="pending">處理中</SelectItem>
                <SelectItem value="failed">失敗</SelectItem>
              </SelectContent>
            </Select>
            <Select value={txProviderFilter} onValueChange={(v) => { setTxProviderFilter(v); setTxPage(1); }}>
              <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">所有付款方式</SelectItem>
                <SelectItem value="helio">Helio</SelectItem>
                <SelectItem value="nowpayments">NOWPayments</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {paymentsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>編號</TableHead>
                  <TableHead>用戶</TableHead>
                  <TableHead>付款方式</TableHead>
                  <TableHead>金額</TableHead>
                  <TableHead>點數</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>日期</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(payments?.items || []).map((tx: any) => (
                  <TableRow key={tx.id}>
                    <TableCell>#{tx.id}</TableCell>
                    <TableCell className="text-sm">{tx.user_email || `用戶 #${tx.user_id}`}</TableCell>
                    <TableCell><Badge variant="outline">{tx.provider}</Badge></TableCell>
                    <TableCell className="font-medium">{formatCurrency(tx.amount)}</TableCell>
                    <TableCell>{tx.credits_amount.toFixed(0)}</TableCell>
                    <TableCell>
                      <Badge variant={tx.status === 'success' ? 'success' : tx.status === 'failed' ? 'destructive' : 'secondary'}>
                        {tx.status === 'success' ? '成功' : tx.status === 'failed' ? '失敗' : '處理中'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDateTime(tx.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {payments && payments.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={txPage <= 1} onClick={() => setTxPage(txPage - 1)}>上一頁</Button>
          <span className="text-sm text-muted-foreground">第 {txPage} 頁，共 {Math.ceil(payments.total / 20)} 頁</span>
          <Button variant="outline" size="sm" disabled={txPage >= Math.ceil(payments.total / 20)} onClick={() => setTxPage(txPage + 1)}>下一頁</Button>
        </div>
      )}
    </div>
  );
}
